import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@pantry/ui';
import { RotateCcwIcon, Trash2Icon, XSquareIcon } from 'lucide-react';
import { useBatchShoppingListItems } from '@pantry/api-client';
import { AnalyticsEvents } from '@pantry/analytics';
import { useAnalytics } from '@/hooks/useAnalytics';
import { UpgradeDialog } from '../premium/UpgradeDialog';
import { toast } from 'sonner';

interface BatchActionsBarProps {
  householdId: string;
  listId: string;
  hasCheckedItems: boolean;
  hasItems: boolean;
  /** Whether this user has premium access */
  hasPremium: boolean;
}

/**
 * Toolbar with batch operations for a shopping list.
 * - Uncheck All: no confirmation
 * - Delete Checked: confirmation dialog (premium)
 * - Clear List: confirmation dialog (premium)
 */
export function BatchActionsBar({
  householdId,
  listId,
  hasCheckedItems,
  hasItems,
  hasPremium,
}: BatchActionsBarProps) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const batchItems = useBatchShoppingListItems();
  const { track } = useAnalytics();

  const [confirmAction, setConfirmAction] = useState<'delete-checked' | 'clear' | null>(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');

  const handleUncheckAll = () => {
    track(AnalyticsEvents.SHOPPING_LIST_BATCH_ACTION, {
      householdId,
      listId,
      action: 'uncheck-all',
    });
    batchItems.mutate(
      { householdId, listId, data: { action: 'uncheck-all' } },
      {
        onError: () => {
          toast.error(t`Failed to update. Please try again.`);
        },
      }
    );
  };

  const handlePremiumAction = (action: 'delete-checked' | 'clear') => {
    if (!hasPremium) {
      track(AnalyticsEvents.SHOPPING_LIST_UPGRADE_PROMPTED, {
        householdId,
        trigger: action === 'delete-checked' ? 'batch_delete' : 'batch_clear',
      });
      setUpgradeFeature(
        action === 'delete-checked' ? t`Delete Checked Items` : t`Clear Shopping List`
      );
      setShowUpgradeDialog(true);
      return;
    }
    setConfirmAction(action);
  };

  const handleConfirm = () => {
    if (!confirmAction) return;
    track(AnalyticsEvents.SHOPPING_LIST_BATCH_ACTION, {
      householdId,
      listId,
      action: confirmAction,
    });
    batchItems.mutate(
      { householdId, listId, data: { action: confirmAction } },
      {
        onSuccess: () => setConfirmAction(null),
        onError: () => {
          toast.error(t`Failed to update. Please try again.`);
          setConfirmAction(null);
        },
      }
    );
  };

  const confirmTitle =
    confirmAction === 'delete-checked' ? t`Delete checked items?` : t`Clear all items?`;

  const confirmDescription =
    confirmAction === 'delete-checked'
      ? t`This will permanently remove all checked items from the list.`
      : t`This will permanently remove all items from the list.`;

  return (
    <>
      <div className="flex flex-wrap gap-2" role="toolbar" aria-label={t`Batch actions`}>
        <Button
          variant="outline"
          size="sm"
          onClick={handleUncheckAll}
          disabled={!hasCheckedItems || batchItems.isPending}
        >
          <RotateCcwIcon className="h-3.5 w-3.5 mr-1.5" />
          <Trans context="action_button">Uncheck All</Trans>
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePremiumAction('delete-checked')}
          disabled={!hasCheckedItems || batchItems.isPending}
          className="text-destructive hover:text-destructive border-destructive/30 hover:border-destructive/50"
        >
          <Trash2Icon className="h-3.5 w-3.5 mr-1.5" />
          <Trans context="action_button">Delete Checked</Trans>
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePremiumAction('clear')}
          disabled={!hasItems || batchItems.isPending}
          className="text-destructive hover:text-destructive border-destructive/30 hover:border-destructive/50"
        >
          <XSquareIcon className="h-3.5 w-3.5 mr-1.5" />
          <Trans context="action_button">Clear List</Trans>
        </Button>
      </div>

      {/* Confirmation dialog */}
      <Dialog open={!!confirmAction} onOpenChange={(isOpen) => !isOpen && setConfirmAction(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{confirmTitle}</DialogTitle>
            <DialogDescription>{confirmDescription}</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-2">
            <Button
              variant="outline"
              onClick={() => setConfirmAction(null)}
              disabled={batchItems.isPending}
            >
              <Trans context="action_button">Cancel</Trans>
            </Button>
            <Button variant="destructive" onClick={handleConfirm} disabled={batchItems.isPending}>
              {batchItems.isPending ? (
                <Trans>Processing...</Trans>
              ) : (
                <Trans context="action_button">Confirm</Trans>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Premium upgrade dialog */}
      <UpgradeDialog
        open={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        feature={upgradeFeature}
        description={t`Batch actions are available with a premium subscription.`}
        onUpgrade={() => void navigate({ to: '/upgrade' })}
      />
    </>
  );
}
